import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FaShieldAlt, FaLock, FaCreditCard, FaUserCheck, FaDatabase, FaHeadset, FaCheckCircle, FaArrowRight } from 'react-icons/fa';

const copy = {
  fr: {
    badge: 'Centre de confiance', title: 'Une expérience locale plus sûre, plus claire et plus fiable.',
    subtitle: 'Forfeo Explore met la sécurité des paiements, la protection des données et la transparence au cœur de chaque réservation, achat et alerte.',
    cta: 'Explorer Forfeo', secondary: 'Besoin d’aide ?',
    pillarsTitle: 'Nos engagements',
    pillars: [
      { icon: 'lock', title: 'Données protégées', text: 'Les données sensibles sont traitées côté serveur et les clés privées ne doivent jamais être exposées dans le navigateur.' },
      { icon: 'card', title: 'Paiements sécurisés', text: 'Les paiements passent par Stripe et les confirmations sont traitées via des webhooks sécurisés.' },
      { icon: 'partner', title: 'Partenaires vérifiés', text: 'Forfeo met en avant des entreprises locales et prépare des mécanismes de validation qualité.' },
      { icon: 'data', title: 'Contrôle utilisateur', text: 'Les utilisateurs peuvent gérer leurs alertes, réservations et préférences depuis leur espace Forfeo.' },
      { icon: 'support', title: 'Support humain', text: 'Forfeo conserve un support clair pour aider les clients et les partenaires en cas de question.' },
      { icon: 'shield', title: 'Architecture renforcée', text: 'Les fonctions sensibles utilisent des validations serveur, des journaux de sécurité et des limites d’utilisation.' },
    ],
    transparencyTitle: 'Transparence Forfeo',
    transparency: [
      'Forfeo ne vend pas directement tous les services affichés : certains achats ou réservations sont finalisés auprès des partenaires.',
      'Les prix, disponibilités et conditions peuvent varier selon les partenaires et fournisseurs externes.',
      'Les confirmations, courriels et liens sont conçus pour aider l’utilisateur à retrouver facilement son expérience.',
    ],
    finalTitle: 'Une plateforme conçue pour inspirer confiance.', finalText: 'Découvre comment Forfeo fonctionne ou explore les offres actuellement disponibles.'
  },
  en: {
    badge: 'Trust Center', title: 'A safer, clearer and more reliable local experience.',
    subtitle: 'Forfeo Explore puts payment security, data protection and transparency at the center of every reservation, purchase and alert.',
    cta: 'Explore Forfeo', secondary: 'Need help?',
    pillarsTitle: 'Our commitments',
    pillars: [
      { icon: 'lock', title: 'Protected data', text: 'Sensitive data is handled server-side and private keys should never be exposed in the browser.' },
      { icon: 'card', title: 'Secure payments', text: 'Payments are processed through Stripe and confirmations are handled through secure webhooks.' },
      { icon: 'partner', title: 'Verified partners', text: 'Forfeo highlights local businesses and is building quality validation mechanisms.' },
      { icon: 'data', title: 'User control', text: 'Users can manage their alerts, reservations and preferences from their Forfeo space.' },
      { icon: 'support', title: 'Human support', text: 'Forfeo keeps support clear and accessible for customers and partners.' },
      { icon: 'shield', title: 'Hardened architecture', text: 'Sensitive functions use server-side validation, security logs and usage limits.' },
    ],
    transparencyTitle: 'Forfeo transparency',
    transparency: [
      'Forfeo does not directly sell every service displayed: some purchases or reservations are finalized with partners.',
      'Prices, availability and conditions may vary depending on partners and external providers.',
      'Confirmations, emails and links are designed to help users easily find their experience details.',
    ],
    finalTitle: 'A platform designed to build trust.', finalText: 'See how Forfeo works or explore the offers currently available.'
  }
};

const icons: Record<string, React.ReactNode> = {
  lock: <FaLock />, card: <FaCreditCard />, partner: <FaUserCheck />, data: <FaDatabase />, support: <FaHeadset />, shield: <FaShieldAlt />
};

export default function Confiance() {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'fr';
  const t = copy[lang];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans">
      <Header />
      <main className="pt-28 pb-24">
        <section className="px-4 py-20 bg-slate-950 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,#2563eb55,transparent_35%)]" />
          <div className="max-w-6xl mx-auto relative">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/10 px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest mb-8"><FaShieldAlt /> {t.badge}</div>
            <h1 className="text-5xl md:text-7xl font-black tracking-tight max-w-5xl mb-6">{t.title}</h1>
            <p className="text-lg md:text-xl text-slate-300 max-w-3xl leading-relaxed mb-10">{t.subtitle}</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/boutique" className="px-8 py-4 rounded-2xl bg-white text-slate-950 font-black hover:bg-slate-100 transition flex items-center justify-center gap-3">{t.cta} <FaArrowRight /></Link>
              <Link to="/aide" className="px-8 py-4 rounded-2xl bg-white/10 border border-white/10 text-white font-black hover:bg-white/15 transition">{t.secondary}</Link>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 py-20">
          <h2 className="text-3xl md:text-4xl font-black mb-10 text-center">{t.pillarsTitle}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.pillars.map((p) => (
              <div key={p.title} className="bg-white rounded-3xl p-7 border border-slate-100 shadow-xl shadow-slate-200/40">
                <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center text-xl mb-5">{icons[p.icon]}</div>
                <h3 className="text-xl font-black mb-3">{p.title}</h3>
                <p className="text-slate-600 leading-relaxed text-sm">{p.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-4 py-10">
          <div className="bg-white rounded-[32px] border border-slate-100 shadow-xl shadow-slate-200/40 p-8 md:p-10">
            <h2 className="text-3xl font-black mb-6">{t.transparencyTitle}</h2>
            <div className="space-y-4">
              {t.transparency.map((item) => (
                <div key={item} className="flex gap-3 items-start text-slate-700">
                  <FaCheckCircle className="text-green-500 mt-1 shrink-0" />
                  <p>{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 text-center py-20">
          <h2 className="text-3xl md:text-4xl font-black mb-4">{t.finalTitle}</h2>
          <p className="text-slate-600 mb-8">{t.finalText}</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/comment-ca-marche" className="px-7 py-4 bg-slate-900 text-white rounded-2xl font-black hover:bg-slate-800 transition">{lang === 'en' ? 'How it works' : 'Comment ça marche'}</Link>
            <Link to="/boutique" className="px-7 py-4 bg-blue-600 text-white rounded-2xl font-black hover:bg-blue-700 transition">{t.cta}</Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
